import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useFormikContext } from 'formik'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { displayAsCurrency } from '../../../../utils/displayAsCurrency'
import { useAdgroupContext } from '../../../../utils/hooks/adGroup/useAdgroupContext'
import { useAdvertiserConfigContext } from '../../../../utils/hooks/advertiser/useAdvertiserConfigContext'
import { AdgroupExtendedSettingsFormInterface } from '../Settings'
import {
  canApplyOverallBudgetDelta,
  getOverallBudgetAfterDelta,
  getOverallLeftToSpend,
  getSpentForBudgetCappingType,
  spentLabelKeyForBudgetCappingType,
} from './overallBudgetCalculator.utils'

interface OverallBudgetCalculatorDialogProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}

const OverallBudgetCalculatorDialog: React.FC<OverallBudgetCalculatorDialogProps> = ({ open, setOpen }) => {
  const t = useAppTranslations()
  const [adGroup] = useAdgroupContext()
  const { config } = useAdvertiserConfigContext()
  const {
    values: { budgetCapping, budgetCappingType },
    setFieldValue,
  } = useFormikContext<AdgroupExtendedSettingsFormInterface>()

  const [deltaInput, setDeltaInput] = useState('')

  const currentBudget = budgetCapping ?? 0
  const budgetMeasures = adGroup.budget?.budgetMeasures
  const currentSpent = getSpentForBudgetCappingType(budgetCappingType, budgetMeasures)
  const spentLabelKey = spentLabelKeyForBudgetCappingType(budgetCappingType)
  const leftToSpend = getOverallLeftToSpend(currentBudget, currentSpent)

  const bounds = useMemo(
    () => ({
      min: Number(config.adGroup.min.budgetCapping) || 0,
      max: Number(config.adGroup.max.budgetCapping) || 0,
    }),
    [config.adGroup.min.budgetCapping, config.adGroup.max.budgetCapping]
  )

  const parsedDelta = deltaInput === '' || deltaInput === '-' ? Number.NaN : Number(deltaInput)
  const newBudget = Number.isNaN(parsedDelta) ? null : getOverallBudgetAfterDelta(currentBudget, parsedDelta)
  const newLeftToSpend = newBudget === null ? null : getOverallLeftToSpend(newBudget, currentSpent)
  const canApply = canApplyOverallBudgetDelta(currentBudget, parsedDelta, bounds)

  useEffect(() => {
    if (open) setDeltaInput('')
  }, [open])

  const handleClose = (): void => setOpen(false)

  const handleApply = (): void => {
    if (!canApply || newBudget === null) return
    void setFieldValue('budgetCapping', newBudget)
    setOpen(false)
  }

  const readoutRow = (label: string, value: string | null): JSX.Element => (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? t('commons.noValueYet')}</Typography>
    </Stack>
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('adgroup.budgetAndDates.overallCalculator.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {readoutRow(t('adgroup.budgetAndDates.overallCalculator.currentBudget'), displayAsCurrency(currentBudget))}
          {readoutRow(t(spentLabelKey), displayAsCurrency(currentSpent))}
          {readoutRow(t('adgroup.budgetAndDates.overallCalculator.leftToSpend'), displayAsCurrency(leftToSpend))}
          <TextField
            label={t('adgroup.budgetAndDates.overallCalculator.amount')}
            helperText={t('adgroup.budgetAndDates.overallCalculator.amountHelper')}
            type="number"
            value={deltaInput}
            onChange={(event) => setDeltaInput(event.target.value)}
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
            }}
          />
          {newBudget !== null && !Number.isNaN(parsedDelta) && (
            <Stack spacing={1} sx={{ pt: 1 }}>
              <Typography variant="subtitle2">{t('adgroup.budgetAndDates.overallCalculator.preview')}</Typography>
              {readoutRow(t('adgroup.budgetAndDates.overallCalculator.newBudget'), displayAsCurrency(newBudget))}
              {readoutRow(
                t('adgroup.budgetAndDates.overallCalculator.newLeftToSpend'),
                displayAsCurrency(newLeftToSpend)
              )}
              {!canApply && (
                <Typography variant="body2" color="error">
                  {t('adgroup.budgetAndDates.overallCalculator.outOfBounds', {
                    min: displayAsCurrency(bounds.min),
                    max: displayAsCurrency(bounds.max),
                  })}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose}>
          {t('commons.actions.cancel')}
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={!canApply}>
          {t('adgroup.budgetAndDates.overallCalculator.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default OverallBudgetCalculatorDialog
